import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { getDashboardSession, getSessionToken, saveDashboardSession, returnToUsers } from '../lib/dashboard-session';
import { getDefaultAuthenticatedRoute } from '../lib/userAccess';
import { EMPTY_ME_SESSION, readMeSession } from './auth-me';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;
const AUTH_REFRESH_COOLDOWN_MS = 30000;
const BACKGROUND_AUTH_REFRESH_MS = 5 * 60 * 1000;

export const AuthProvider = ({ children }) => {
  // Every cached query (batches, agents, calls, home cards...) is scoped to the
  // signed-in account by the token on the request, but React Query keys don't
  // include the account -- so without wiping the cache on any identity change,
  // the next account sees the previous one's data until each query refetches.
  const queryClient = useQueryClient();
  const [user, setUser] = useState(null);
  const [account, setAccount] = useState(null);
  // Effective permissions from /auth/me: entitlements INTERSECT (role UNION inline).
  // These gate what the UI *offers*, never what it is allowed to do -- the server
  // re-checks every request. Their job is to stop handing someone a Take Over button
  // that will only ever answer 403.
  const [permissions, setPermissions] = useState([]);
  // The caller's role INSIDE their organisation ("owner" | "member"), which is not
  // the same thing as user.role -- that one is the platform role, where "admin" lives.
  const [roleId, setRoleId] = useState(null);
  // R3 fields of /auth/me: backend-resolved switches, switchable orgs, entitlements.
  const [meSession, setMeSession] = useState(EMPTY_ME_SESSION);
  const [dashboardSession] = useState(getDashboardSession);
  const [token, setToken] = useState(getSessionToken);
  const [loading, setLoading] = useState(true);
  const [vpnDetected, setVpnDetected] = useState(false);
  const [networkInfo, setNetworkInfo] = useState(null);
  const [recheckingNetwork, setRecheckingNetwork] = useState(false);
  const refreshInFlightRef = useRef({ token: null, promise: null });
  // The last /auth/me read, so an adopted session can hand other tabs its permissions
  // and switches along with the token.
  const meSnapshotRef = useRef(null);
  const lastAuthRefreshAtRef = useRef(0);
  const userRef = useRef(null);
  // Tracks whether the initial hydration from localStorage has already run.
  // This prevents the initAuth effect from firing again after login() / register()
  // set a new token in-session, which could race-clear a brand-new account.
  const hasHydratedRef = useRef(false);
  // Bumped when this tab adopts another session (org switch, invite accept) so an
  // /auth/me still in flight for the old token cannot land after the new one.
  const identityEpochRef = useRef(0);

  useEffect(() => {
    userRef.current = user;
  }, [user]);

  const clearAuthState = useCallback(() => {
    if (getDashboardSession()) {
      returnToUsers();
      return;
    }
    localStorage.removeItem('token');
    try { localStorage.removeItem('techladder_demo_mode'); } catch {}
    setToken(null);
    setUser(null);
    setAccount(null);
    setPermissions([]);
    setRoleId(null);
    setMeSession(EMPTY_ME_SESSION);
    setVpnDetected(false);
    setNetworkInfo(null);
    userRef.current = null;
    lastAuthRefreshAtRef.current = 0;
    refreshInFlightRef.current = { token: null, promise: null };
    queryClient.clear();
  }, [queryClient]);

  const refreshUser = useCallback(async (explicitToken = token, options = {}) => {
    const targetToken = explicitToken || token;
    if (!targetToken) {
      setUser(null);
      userRef.current = null;
      return null;
    }

    const force = options?.force === true;
    const now = Date.now();
    if (
      !force
      && userRef.current
      && now - lastAuthRefreshAtRef.current < AUTH_REFRESH_COOLDOWN_MS
    ) {
      return userRef.current;
    }

    if (
      refreshInFlightRef.current.promise
      && refreshInFlightRef.current.token === targetToken
    ) {
      return refreshInFlightRef.current.promise;
    }

    const epoch = identityEpochRef.current;
    const request = axios
      .get(`${API_URL}/api/v2/auth/me`, {
        headers: { Authorization: `Bearer ${targetToken}` }
      })
      .then((response) => {
        if (epoch !== identityEpochRef.current) return userRef.current;
        const nextUser = response.data.user;
        const nextAccount = response.data.account;
        if (nextUser && (nextUser.demo_mode === true || nextUser.demo_mode === 'true' || nextUser.demo_mode === 1)) {
          try { localStorage.setItem('techladder_demo_mode', 'true'); } catch {}
        } else {
          try { localStorage.removeItem('techladder_demo_mode'); } catch {}
        }
        setUser(nextUser);
        setAccount(nextAccount);
        if (nextUser && typeof nextUser.vpn_detected === 'boolean') {
          setVpnDetected(nextUser.vpn_detected);
          setNetworkInfo(nextUser.network_info || null);
        }
        setPermissions(
          Array.isArray(response.data.permissions) ? response.data.permissions : []
        );
        setRoleId(response.data.role_id ?? null);
        setMeSession(readMeSession(response.data));
        meSnapshotRef.current = {
          permissions: Array.isArray(response.data.permissions) ? response.data.permissions : [],
          roleId: response.data.role_id ?? null,
          me: response.data,
        };
        userRef.current = nextUser;
        lastAuthRefreshAtRef.current = Date.now();
        return nextUser;
      })
      .finally(() => {
        if (refreshInFlightRef.current.token === targetToken) {
          refreshInFlightRef.current = { token: null, promise: null };
        }
      });

    refreshInFlightRef.current = { token: targetToken, promise: request };
    return request;
  }, [token]);

  useEffect(() => {
    // Only hydrate once from localStorage. Subsequent token changes (login,
    // register, OTP) are handled inside those functions and don't need this.
    if (hasHydratedRef.current) return;
    hasHydratedRef.current = true;

    const initAuth = async () => {
      if (dashboardSession && !token) {
        returnToUsers();
        return;
      }
      if (token) {
        try {
          await refreshUser(token, { force: true });
        } catch (error) {
          clearAuthState();
        }
      }
      try {
        await recheckNetworkConnection();
      } catch {}
      setLoading(false);
    };
    initAuth();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!token) return undefined;

    const handleFocus = () => {
      refreshUser(undefined, { force: true }).catch(() => {
        clearAuthState();
      });
      recheckNetworkConnection().catch(() => {});
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        handleFocus();
      }
    };

    const refreshInterval = window.setInterval(handleFocus, BACKGROUND_AUTH_REFRESH_MS);
    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.clearInterval(refreshInterval);
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, refreshUser, clearAuthState]);

  // Refs for BroadcastChannel handler to avoid stale closures
  const tokenRef = useRef(token);
  const accountRef = useRef(account);
  const loadingRef = useRef(loading);
  useEffect(() => { tokenRef.current = token; }, [token]);
  useEffect(() => { accountRef.current = account; }, [account]);
  useEffect(() => { loadingRef.current = loading; }, [loading]);

  useEffect(() => {
    const channel = new BroadcastChannel('auth_sync_channel');

    channel.onmessage = (event) => {
      if (!event.data) return;
      // A customer view belongs to this tab and must never broadcast its identity.
      if (dashboardSession) {
        if (event.data.type === 'LOGOUT' ||
            (event.data.type === 'SYNC_STATE' && event.data.payload?.token !== dashboardSession.parentToken)) {
          returnToUsers();
        }
        return;
      }
      
      switch (event.data.type) {
        case 'REQUEST_STATE':
          // Another tab is asking for state — respond if we have it
          if (tokenRef.current && userRef.current) {
            channel.postMessage({
              type: 'SYNC_STATE',
              payload: { token: tokenRef.current, user: userRef.current, account: accountRef.current }
            });
          }
          break;
          
        case 'SYNC_STATE':
          // We received state from another tab
          if (event.data.payload) {
            const { token: newToken, user: newUser, account: newAccount, me } = event.data.payload;
            if (newToken && newUser) {
              // Another tab switched accounts -- drop this tab's cache so it
              // doesn't keep showing the previous account's data.
              if (newToken !== tokenRef.current) queryClient.clear();
              localStorage.setItem('token', newToken);
              setToken(newToken);
              setUser(newUser);
              setAccount(newAccount);
              userRef.current = newUser;
              tokenRef.current = newToken;
              accountRef.current = newAccount;
              // Only adopted sessions carry this; a plain login broadcast behaves as before.
              if (me) {
                setPermissions(me.permissions);
                setRoleId(me.roleId);
                setMeSession(readMeSession(me.me));
              }
              if (loadingRef.current) setLoading(false);
            }
          }
          break;
          
        case 'LOGOUT':
          // Another tab logged out
          clearAuthState();
          break;
      }
    };

    // If this tab doesn't have user data yet, ask other tabs
    if (!dashboardSession && !userRef.current) {
      channel.postMessage({ type: 'REQUEST_STATE' });
    }

    return () => channel.close();
  // Only mount/unmount — refs handle dynamic values
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clearAuthState, dashboardSession]);

  useEffect(() => {
    if (!dashboardSession) return undefined;
    const checkParentSession = (event) => {
      if (event.key === 'token' || event.key === null) {
        if (localStorage.getItem('token') !== dashboardSession.parentToken) returnToUsers();
      }
    };
    window.addEventListener('storage', checkParentSession);
    return () => window.removeEventListener('storage', checkParentSession);
  }, [dashboardSession]);

  // Broadcast state changes helper
  const broadcastState = useCallback((newToken, newUser, newAccount, me) => {
    const channel = new BroadcastChannel('auth_sync_channel');
    channel.postMessage({
      type: 'SYNC_STATE',
      payload: { token: newToken, user: newUser, account: newAccount, ...(me ? { me } : {}) }
    });
    channel.close();
  }, []);

  // Replace this tab's session with a server-minted token for another organization.
  // Caches are wiped first so nothing from the old org renders under the new identity;
  // supervision audio disconnects because its provider is keyed on the organization.
  const adoptSession = async (accessToken) => {
    identityEpochRef.current += 1;
    queryClient.clear();
    localStorage.setItem('token', accessToken);
    setToken(accessToken);
    tokenRef.current = accessToken;
    const fullUser = await refreshUser(accessToken, { force: true });
    const snapshot = meSnapshotRef.current;
    broadcastState(accessToken, fullUser, snapshot?.me?.account ?? accountRef.current, snapshot);
    return fullUser;
  };

  const switchOrganization = async (organizationId) => {
    const response = await axios.post(
      `${API_URL}/api/v2/auth/switch-organization`,
      { organization_id: organizationId },
      { headers: { Authorization: `Bearer ${token}` } },
    );
    return adoptSession(response.data.access_token);
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/api/v1/auth/login`, { email: email.trim(), password });
    const { access_token, user: userData } = response.data;
    // Drop any cache from a prior session before the new account's queries run,
    // in case this login happened without an explicit logout first.
    queryClient.clear();
    localStorage.setItem('token', access_token);
    setToken(access_token);
    
    const fullUser = await refreshUser(access_token, { force: true });
    broadcastState(access_token, fullUser, accountRef.current);
    return fullUser;
  };

  const register = async (name, email, password) => {
    const response = await axios.post(`${API_URL}/api/v1/auth/register`, { name, email, password });
    const { access_token, user: userData } = response.data;
    queryClient.clear();
    localStorage.setItem('token', access_token);
    setToken(access_token);
    
    const fullUser = await refreshUser(access_token, { force: true });
    broadcastState(access_token, fullUser, accountRef.current);
    return fullUser;
  };

  const sendOtp = async (phone) => {
    const response = await axios.post(`${API_URL}/api/v1/auth/send-otp`, { phone });
    return response.data;
  };

  const verifyOtp = async (phone, otp) => {
    const response = await axios.post(`${API_URL}/api/v1/auth/verify-otp`, { phone, otp });
    const { access_token, user: userData } = response.data;
    queryClient.clear();
    localStorage.setItem('token', access_token);
    setToken(access_token);
    
    const fullUser = await refreshUser(access_token, { force: true });
    broadcastState(access_token, fullUser, accountRef.current);
    return fullUser;
  };

  const openUserDashboard = async (userId) => {
    const response = await axios.post(`${API_URL}/api/v1/dashboard-access/users/${encodeURIComponent(userId)}/dashboard-session`, {}, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const accessToken = response.data.access_token;
    // Validate and resolve the destination before changing this tab's identity.
    const { data } = await axios.get(`${API_URL}/api/v2/auth/me`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    if (localStorage.getItem('token') !== token) throw new Error('Login session changed. Please refresh.');
    saveDashboardSession({
      accessToken, parentToken: token, userId,
      userLabel: data.user?.email || data.user?.name || userId,
    });
    window.location.replace(getDefaultAuthenticatedRoute(data.user));
  };

  const recheckNetworkConnection = useCallback(async () => {
    try {
      setRecheckingNetwork(true);
      const targetToken = token || localStorage.getItem('token');
      const rawDeviceTz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Kolkata';
      const deviceTz = rawDeviceTz === 'Asia/Calcutta' ? 'Asia/Kolkata' : rawDeviceTz;
      const headers = {
        'x-device-timezone': deviceTz,
        ...(targetToken ? { Authorization: `Bearer ${targetToken}` } : {}),
      };

      // Detect public egress IP (essential for local dev on loopback or transparent proxies)
      let publicIp = null;
      try {
        const ipifyResp = await axios.get('https://api.ipify.org?format=json', { timeout: 2000 });
        if (ipifyResp.data?.ip) {
          publicIp = ipifyResp.data.ip;
          headers['x-public-ip'] = publicIp;
        }
      } catch {
        // Fallback to direct network check if external IP resolver is unreachable
      }

      const res = await axios.get(`${API_URL}/api/v2/auth/network-check`, { headers });
      const isVpn = Boolean(res.data?.vpn_detected);
      setVpnDetected(isVpn);
      setNetworkInfo({
        is_vpn: isVpn,
        ip: res.data?.ip || publicIp,
        provider: res.data?.provider,
        region: res.data?.region,
      });
      return res.data;
    } catch (err) {
      console.error("Network check failed", err);
      return null;
    } finally {
      setRecheckingNetwork(false);
    }
  }, [token]);

  const logout = () => {
    if (dashboardSession) {
      returnToUsers();
      return;
    }
    clearAuthState();
    const channel = new BroadcastChannel('auth_sync_channel');
    channel.postMessage({ type: 'LOGOUT' });
    channel.close();
  };

  const value = {
    dashboardSession,
    openUserDashboard,
    user,
    account,
    permissions,
    roleId,
    features: meSession.features,
    organizations: meSession.organizations,
    membershipStatus: meSession.membershipStatus,
    resourceScope: meSession.resourceScope,
    organization: meSession.organization,
    adoptSession,
    switchOrganization,
    token,
    loading,
    login,
    register,
    sendOtp,
    verifyOtp,
    refreshUser,
    logout,
    isAuthenticated: !!token && !!user,
    vpnDetected,
    networkInfo,
    recheckingNetwork,
    recheckNetworkConnection
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

/**
 * Whether the current user holds `permission`.
 *
 * Lives here rather than in FeatureFlagContext because AuthProvider is the one actually
 * mounted, and it already makes the /auth/me request these come from -- a second provider
 * would mean a second identical call, and (as happened) silently returning false for
 * everyone if nobody remembered to mount it.
 *
 * Returns false while /auth/me is in flight, so a control never appears before we know
 * whether it should.
 */
export const useHasPermission = (permission) => {
  const { permissions } = useAuth();
  return Array.isArray(permissions) && permissions.includes(permission);
};
